<template>
  <v-dialog v-model="dialog" max-width="500" :persistent="loading" scrollable>
    <v-card>
      <v-card-title>Editar Lançamento</v-card-title>
      <v-card-text>
        <v-chip
          v-if="entry.invoiceId"
          x-small
          label
          color="primary"
          outlined
          class="mb-3"
        >
          Vinculado à fatura
        </v-chip>

        <v-form v-model="valid" @submit.prevent>
          <div class="text-caption text--secondary mb-1">Tipo</div>
          <v-btn-toggle
            v-model="form.type"
            mandatory
            dense
            class="d-flex mb-4"
            :disabled="loading || isInvoiceLinked"
          >
            <v-btn value="INCOME" class="flex-1" :color="form.type === 'INCOME' ? 'success' : undefined">
              <v-icon left small :color="form.type === 'INCOME' ? 'white' : 'success'">
                mdi-arrow-down-bold
              </v-icon>
              Entrada
            </v-btn>
            <v-btn value="EXPENSE" class="flex-1" :color="form.type === 'EXPENSE' ? 'error' : undefined">
              <v-icon left small :color="form.type === 'EXPENSE' ? 'white' : 'error'">
                mdi-arrow-up-bold
              </v-icon>
              Saída
            </v-btn>
          </v-btn-toggle>

          <v-combobox
            v-if="!isInvoiceLinked"
            v-model="form.description"
            :items="descriptionSuggestions"
            item-text="description"
            item-value="description"
            :return-object="false"
            :loading="loadingDescriptions"
            label="Descrição"
            outlined
            dense
            hide-selected
            :rules="[(v) => !!descriptionText(v) || 'Descrição é obrigatória']"
            :disabled="loading"
            @change="onDescriptionChange"
          />
          <v-text-field
            v-else
            v-model="form.description"
            label="Descrição"
            outlined
            dense
            readonly
            :disabled="loading"
          />

          <CashCategoryField
            v-if="dialog"
            ref="categoryField"
            v-model="form.categoryId"
            :type="form.type"
            :company-id="entry.Category && entry.Category.companyId"
            :selected="entry.Category"
            :disabled="loading || isInvoiceLinked"
          />

          <v-select
            v-model="form.status"
            :items="statusItems"
            label="Status"
            outlined
            dense
            :disabled="loading"
            @change="onStatusChange"
          />

          <v-text-field
            v-model="form.expectedAmount"
            label="Valor previsto"
            type="number"
            step="0.01"
            min="0"
            prefix="R$"
            outlined
            dense
            :rules="amountRules"
            :disabled="loading"
            @paste="onAmountPaste($event, 'expectedAmount')"
            @blur="onAmountBlur('expectedAmount')"
            @input="syncSettledFields"
          />
          <v-text-field
            v-model="form.dueDate"
            label="Data de vencimento"
            type="date"
            outlined
            dense
            :disabled="loading"
            @input="onDueDateChange"
          />

          <template v-if="form.status === 'SETTLED'">
            <v-text-field
              v-model="form.settledAmount"
              :label="form.type === 'EXPENSE' ? 'Valor pago' : 'Valor recebido'"
              type="number"
              step="0.01"
              min="0"
              prefix="R$"
              outlined
              dense
              class="mt-2"
              :rules="amountRules"
              :disabled="loading"
              @paste="onAmountPaste($event, 'settledAmount')"
              @blur="onAmountBlur('settledAmount')"
            />
            <v-text-field
              v-model="form.settledAt"
              :label="form.type === 'EXPENSE' ? 'Data do pagamento' : 'Data do recebimento'"
              type="date"
              outlined
              dense
              hide-details
              :rules="[(v) => !!v || 'Data é obrigatória']"
              :disabled="loading"
            />
            <SettledDateChips
              v-model="form.settledAt"
              :due-date="form.dueDate"
              :disabled="loading"
            />
          </template>

          <v-textarea
            v-model="form.notes"
            label="Observações"
            outlined
            dense
            rows="2"
            :disabled="loading"
          />
        </v-form>
        <v-alert v-if="error" type="error" dense text class="mt-2">
          {{ error }}
        </v-alert>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn text small @click="close" :disabled="loading">Cancelar</v-btn>
        <v-btn
          color="primary"
          depressed
          small
          :loading="loading"
          :disabled="!valid"
          @click="submit"
        >
          Salvar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import moment from "moment";
import CASH from "@/services/ultimate/cash.js";
import { parseBrazilianCurrency } from "@/utils/parseCurrency";
import CashCategoryField from "@/components/ultimate/cash/CashCategoryField.vue";
import { withLocalDateISO } from "@/utils/dateOnlyToLocalISO";
import SettledDateChips from "@/components/ultimate/cash/SettledDateChips.vue";

export default {
  name: "CashEntryEditModal",
  components: {
    SettledDateChips,
    CashCategoryField,
  },
  data() {
    return {
      dialog: false,
      loading: false,
      loadingDescriptions: false,
      valid: false,
      error: null,
      descriptionSuggestions: [],
      entry: {},
      form: {},
      amountRules: [
        (v) => v !== "" && v != null || "Valor é obrigatório",
        (v) => parseFloat(v) > 0 || "Valor deve ser maior que zero",
      ],
      statusItems: [
        { text: "Pendente", value: "PENDING" },
        { text: "Recebido/Pago", value: "SETTLED" },
        { text: "Cancelado", value: "CANCELLED" },
      ],
    };
  },
  computed: {
    isInvoiceLinked() {
      return !!this.entry.invoiceId;
    },
    descriptionReference() {
      if (!this.form.dueDate) return moment().format("MM/YYYY");
      return moment(this.form.dueDate).format("MM/YYYY");
    },
  },
  methods: {
    formatDate(value) {
      if (!value) return null;
      return moment(value).format("YYYY-MM-DD");
    },
    buildForm(entry) {
      return {
        type: entry.type,
        description: entry.description,
        categoryId: entry.categoryId || null,
        status: entry.status,
        expectedAmount: entry.expectedAmount,
        dueDate: this.formatDate(entry.dueDate) || moment().format("YYYY-MM-DD"),
        settledAmount: entry.settledAmount,
        settledAt:
          this.formatDate(entry.settledAt) ||
          this.formatDate(entry.dueDate) ||
          moment().format("YYYY-MM-DD"),
        notes: entry.notes || "",
      };
    },
    applyParsedAmount(field, rawValue) {
      const { amount, isNegative } = parseBrazilianCurrency(rawValue);
      if (amount == null) return false;

      this.form[field] = amount;
      if (isNegative && !this.isInvoiceLinked) this.form.type = "EXPENSE";
      this.syncSettledFields();
      return true;
    },
    onAmountPaste(event, field) {
      const text = event.clipboardData.getData("text");
      if (this.applyParsedAmount(field, text)) {
        event.preventDefault();
      }
    },
    onAmountBlur(field) {
      this.applyParsedAmount(field, this.form[field]);
    },
    syncSettledFields() {
      if (this.form.status !== "SETTLED") return;
      if (this.form.settledAmount == null) {
        this.form.settledAmount = this.form.expectedAmount;
      }
    },
    onStatusChange(status) {
      if (status === "SETTLED") {
        this.form.settledAmount = this.form.settledAmount ?? this.form.expectedAmount;
        this.form.settledAt =
          this.form.settledAt ||
          this.form.dueDate ||
          moment().format("YYYY-MM-DD");
      }
    },
    onDueDateChange() {
      this.loadDescriptions();
    },
    descriptionText(value) {
      if (typeof value === "string") return value.trim();
      if (value && typeof value.description === "string") {
        return value.description.trim();
      }
      return "";
    },
    onDescriptionChange(value) {
      if (this.isInvoiceLinked) return;

      const description = this.descriptionText(value);
      if (this.form.description !== description) {
        this.form.description = description;
      }
      if (!description) return;

      const match = this.descriptionSuggestions.find(
        (item) => item.description === description
      );
      if (!match) return;

      if (match.type) this.form.type = match.type;
      if (match.categoryId && this.$refs.categoryField) {
        this.$refs.categoryField.apply(match.categoryId);
      }
    },
    async loadDescriptions() {
      if (this.isInvoiceLinked) return;
      try {
        this.loadingDescriptions = true;
        const response = await CASH.getDescriptions({
          reference: this.descriptionReference,
        });
        this.descriptionSuggestions = response.descriptions || [];
      } catch (error) {
        console.error(error);
        this.descriptionSuggestions = [];
      } finally {
        this.loadingDescriptions = false;
      }
    },
    open(entry) {
      this.entry = entry;
      this.form = this.buildForm(entry);
      this.error = null;
      this.dialog = true;
      this.loadDescriptions();
    },
    close() {
      this.dialog = false;
    },
    async submit() {
      try {
        this.loading = true;
        this.error = null;
        this.applyParsedAmount("expectedAmount", this.form.expectedAmount);
        if (this.form.status === "SETTLED") {
          this.applyParsedAmount("settledAmount", this.form.settledAmount);
        }
        await CASH.update(
          this.entry.id,
          withLocalDateISO({
            ...this.form,
            description: this.descriptionText(this.form.description),
          })
        );
        this.$emit("updated");
        this.close();
      } catch (error) {
        this.error = error.message || "Erro ao atualizar lançamento";
      } finally {
        this.loading = false;
      }
    },
  },
  mounted() {
    this.$parent.$on("cash-entry-edit", this.open);
  },
  beforeDestroy() {
    this.$parent.$off("cash-entry-edit", this.open);
  },
};
</script>
